import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { Bot, Zap, Users, Phone, Loader, Clock, Code, TrendingUp, Cpu, ArrowRight, X, Menu, Globe, Shield, Cookie } from 'lucide-react';

// --- Configuration & Data ---

// Navigation items used for routing and rendering
const navItems = [
  { name: 'Home', page: 'home', icon: Bot },
  { name: 'Solutions', page: 'services', icon: Zap }, // Changed from Expertise to Solutions
  { name: 'About Us', page: 'about', icon: Users },
  { name: 'Contact', page: 'contact', icon: Phone },
];

// Content now structured into three main consultative pillars
const servicePillars = [
    {
        name: 'I. Workflow & AI Optimization',
        description: 'We design custom, streamlined workflows to eliminate repetitive tasks, focusing on immediate time-savings and operational clarity.',
        icon: Zap,
        services: [
            { name: 'Custom Automation Solutions', detail: 'Tailored solutions built specifically for your unique, complex business processes.' },
            { name: 'No-Code/Low-Code Implementation', detail: 'Leveraging tools like n8n and Make.com for quick, affordable, and flexible workflow deployment.' },
            { name: 'API Integrations', detail: 'Connecting disparate software systems (CRM, ERP, Finance) to ensure seamless data flow.' },
        ]
    },
    {
        name: 'II. Custom Development & Utilities',
        description: 'When off-the-shelf tools fall short, we build robust, production-ready custom software and utilities from the ground up.',
        icon: Code,
        services: [
            { name: 'Fully Coded Custom Utilities', detail: 'Developing custom scripts, microservices, and apps for hyper-specific operational needs.' },
            { name: 'Software Implementation Consulting', detail: 'Strategic guidance and hands-on deployment of new software to maximize adoption and efficiency.' },
        ]
    },
    {
        name: 'III. Technical Infrastructure & Security',
        description: 'We ensure the technical foundation of your business is modern, secure, and scalable to support future growth.',
        icon: Globe,
        services: [
            { name: 'IDP & SSO Configuration', detail: 'Setting up secure Identity Providers and Single Sign-On for centralized access management.' },
            { name: 'Server Implementation & Cloud Strategy', detail: 'Planning and deployment of secure cloud or dedicated server environments for maximum reliability.' },
            { name: 'Network Consulting', detail: 'Advising on foundational network architecture to optimize speed, security, and scalability.' },
        ]
    }
];

// --- Tracking Configuration ---
const FACEBOOK_PIXEL_ID = '1383097013687341';
const GOOGLE_ANALYTICS_ID = 'G-ZFJL9B1YWF';

// Cookie utilities
const CookieManager = {
  getCookiePreferences: () => {
    const stored = localStorage.getItem('cookiePreferences');
    return stored ? JSON.parse(stored) : null;
  },
  setCookiePreferences: (preferences) => {
    localStorage.setItem('cookiePreferences', JSON.stringify(preferences));
  },
  getHasConsented: () => {
    const prefs = CookieManager.getCookiePreferences();
    return prefs && (prefs.analytics || prefs.marketing);
  }
};

// Initialize Facebook Pixel using Meta's official implementation
const initializeFacebookPixel = () => {
  if (window.fbq) return;

  // Meta Pixel initialization
  (function(f,b,e,v,n,t,s){
    if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)
  })(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');

  window.fbq('init', FACEBOOK_PIXEL_ID);
  window.fbq('track', 'PageView');
};

// Track event with Facebook Pixel
const trackFacebookEvent = (eventName, data = {}) => {
  if (CookieManager.getCookiePreferences()?.marketing && window.fbq) {
    window.fbq('track', eventName, data);
  }
};

// Initialize Google Analytics
const initializeGoogleAnalytics = () => {
  if (window.gtag) return;
  window.dataLayer = window.dataLayer || [];
  function gtag(){window.dataLayer.push(arguments);}
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', GOOGLE_ANALYTICS_ID, { 'anonymize_ip': true });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ANALYTICS_ID}`;
  document.head.appendChild(script);
};

// Track event with Google Analytics
const trackGoogleEvent = (eventName, eventData = {}) => {
  if (CookieManager.getCookiePreferences()?.analytics && window.gtag) {
    window.gtag('event', eventName, eventData);
  }
};

// Track page view
const trackPageView = (pageName) => {
  trackFacebookEvent('PageView');
  trackGoogleEvent('page_view', { page_title: pageName });
};

// --- Utility Components ---

/** Cookie Consent Banner */
const CookieConsent = ({ onConsent }) => {
  const [isVisible, setIsVisible] = useState(() => !CookieManager.getHasConsented());
  const [showDetails, setShowDetails] = useState(false);

  const handleAcceptAll = () => {
    const prefs = { analytics: true, marketing: true };
    CookieManager.setCookiePreferences(prefs);
    initializeGoogleAnalytics();
    initializeFacebookPixel();
    setIsVisible(false);
    onConsent?.(prefs);
  };

  const handleRejectAll = () => {
    const prefs = { analytics: false, marketing: false };
    CookieManager.setCookiePreferences(prefs);
    setIsVisible(false);
    onConsent?.(prefs);
  };

  const handleSavePreferences = (analytics, marketing) => {
    const prefs = { analytics, marketing };
    CookieManager.setCookiePreferences(prefs);
    if (analytics) initializeGoogleAnalytics();
    if (marketing) initializeFacebookPixel();
    setIsVisible(false);
    onConsent?.(prefs);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 bg-slate-900 border-t border-cyan-800/50 shadow-2xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-start justify-between gap-6">
          <div className="flex items-start gap-4 flex-1">
            <Cookie className="h-6 w-6 text-cyan-400 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-lg font-bold text-white mb-2">Cookie Preferences</h3>
              <p className="text-gray-300 text-sm mb-3">
                We use cookies to enhance your experience. Some are essential for the site to function, while others help us track analytics and improve our marketing.
              </p>
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="text-cyan-400 hover:text-cyan-300 text-sm font-semibold underline"
              >
                {showDetails ? 'Hide Details' : 'Show Details'}
              </button>

              {showDetails && (
                <div className="mt-4 space-y-2 text-sm text-gray-300 bg-slate-800/50 p-3 rounded">
                  <p><strong>Analytics:</strong> Google Analytics - tracks user behavior (pages visited, time on site)</p>
                  <p><strong>Marketing:</strong> Facebook Pixel - tracks conversions and enables targeted ads</p>
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-3 flex-shrink-0">
            <button
              onClick={handleRejectAll}
              className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition"
            >
              Reject All
            </button>
            <button
              onClick={handleAcceptAll}
              className="px-6 py-2 text-sm font-medium bg-cyan-600 hover:bg-cyan-700 text-white rounded transition"
            >
              Accept All
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Reusable Primary CTA Button - Updated to Cyan */
const PrimaryButton = ({ onClick, children, className = '' }) => (
    <button
        onClick={onClick}
        className={`bg-cyan-600 hover:bg-cyan-700 text-white font-bold py-3 px-10 rounded-full text-lg transition duration-300 transform hover:scale-[1.05] shadow-2xl shadow-cyan-900/50 flex items-center justify-center ${className}`}
    >
        {children}
    </button>
);


// --- Components ---

/** Header component with navigation and logo - Updated to Slate/Cyan */
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const getRoutePath = (page) => {
    if (page === 'home') return '/';
    if (page === 'services') return '/solutions';
    if (page === 'about') return '/about';
    if (page === 'contact') return '/contact';
    return '/';
  };

  const isActive = (page) => {
    const currentPath = location.pathname;
    return getRoutePath(page) === currentPath || (page === 'home' && currentPath === '/');
  };

  return (
    <header className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-sm shadow-xl border-b border-cyan-800/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center cursor-pointer hover:opacity-80 transition">
            <Bot className="h-8 w-8 text-cyan-400 mr-2" />
            <div className="text-3xl font-black text-white tracking-wider">
              Skyewire<span className="text-cyan-400">Solutions</span>
            </div>
          </Link>

          <div className="hidden md:flex items-center space-x-12">
            <nav className="flex space-x-6">
              {navItems.filter(item => item.page !== 'contact').map((item) => (
                <Link
                  key={item.page}
                  to={getRoutePath(item.page)}
                  className={`flex items-center text-base font-semibold transition duration-200 py-1 border-b-2 ${
                    isActive(item.page)
                      ? 'text-cyan-400 border-cyan-400'
                      : 'text-gray-300 border-transparent hover:text-cyan-400 hover:border-cyan-400/50'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </nav>
            <Link to="/contact">
              <button className="bg-cyan-600 hover:bg-cyan-700 text-white font-bold py-3 px-10 rounded-full text-lg transition duration-300 transform hover:scale-[1.05] shadow-2xl shadow-cyan-900/50 flex items-center justify-center">
                Start Project
                <ArrowRight className="h-4 w-4 ml-2" />
              </button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-gray-300 hover:text-cyan-400"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-menu"
          >
            {isMenuOpen ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {isMenuOpen && (
        <div id="mobile-menu" className="md:hidden bg-slate-900/90 py-2 border-t border-cyan-800">
          {navItems.map((item) => (
            <Link
              key={item.page}
              to={getRoutePath(item.page)}
              onClick={() => setIsMenuOpen(false)}
              className={`block w-full text-left px-6 py-3 text-base font-medium transition duration-200 ${
                isActive(item.page)
                  ? 'text-cyan-400 bg-cyan-900/30'
                  : 'text-gray-300 hover:text-cyan-400 hover:bg-slate-800'
              }`}
            >
                <item.icon className="h-5 w-5 inline mr-3" />
                {item.name}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
};

/** Footer component - Updated to Slate/Cyan */
const Footer = () => (
    <footer className="bg-slate-900 border-t border-cyan-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-gray-400">
            <p className="text-2xl font-black mb-4">Skyewire<span className="text-cyan-400">Solutions</span></p>
            <div className="flex justify-center space-x-6 mb-4">
                <Link to="/contact" className="hover:text-cyan-400 transition text-sm">Contact Us</Link>
                <button onClick={() => alert('Privacy Policy content goes here.')} className="hover:text-cyan-400 transition text-sm">Privacy Policy</button>
                <button onClick={() => alert('Terms of Service content goes here.')} className="hover:text-cyan-400 transition text-sm">Terms of Use</button>
            </div>
            <p className="text-xs text-gray-500 mt-6">&copy; {new Date().getFullYear()} Skyewire Solutions. All rights reserved.</p>
        </div>
    </footer>
);

/** Home Page View - Redesigned Layout and Colors */
const Home = () => (
    <div className="pt-0 pb-20 bg-slate-900">
        {/* Section 1: Hero - High Impact Cyan Background */}
        <section className="min-h-[85vh] flex items-center w-full bg-slate-900" 
            style={{ 
                backgroundImage: 'radial-gradient(at 50% 10%, #064E3B 0%, #0F172A 80%)', // Subtle green/cyan glow
                clipPath: 'polygon(0 0, 100% 0, 100% 85%, 0% 100%)' // Angled bottom cut
            }}>
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20">
                <p className="text-lg font-medium text-cyan-400 uppercase tracking-widest mb-4">Consulting & Custom Solutions</p>
                <h1 className="text-5xl md:text-7xl font-extrabold text-white mb-6 leading-tight tracking-tight">
                    Scale Your Business, <span className="text-cyan-400">Not Your Manual Work.</span>
                </h1>
                <h2 className="text-xl md:text-2xl font-light text-gray-300 mb-12 max-w-4xl mx-auto">
                    We eliminate repetitive tasks and build **custom AI-powered workflows** and stable infrastructure that growing SMBs truly need.
                </h2>
                <Link to="/contact">
                    <PrimaryButton>
                        Start Your Free Workflow Audit
                        <ArrowRight className="h-5 w-5 ml-3" />
                    </PrimaryButton>
                </Link>
            </div>
        </section>

        {/* Section 2: The Pain Point - Clean Grid */}
        <section className="w-full bg-slate-900 py-20 md:py-32 pt-20">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                <h3 className="text-4xl md:text-5xl font-bold text-white mb-12">
                    Facing <span className="text-red-400">The Efficiency Drain?</span>
                </h3>
                <div className="grid md:grid-cols-3 gap-8">
                    <PainCard icon={Clock} title="Wasted Time" description="Staff are spending 10+ hours a week on predictable, manual data transfers and entry." />
                    <PainCard icon={X} title="Costly Errors" description="Human-error rates rise, leading to missed leads, inaccurate reports, and compliance risks." />
                    <PainCard icon={Code} title="Scaling Blockade" description="Your existing systems don't talk to each other, making growth dependent on hiring more staff." />
                </div>
            </div>
        </section>

        {/* Section 3: Value Proposition - Custom Background & Layout */}
        <section className="bg-slate-800 py-20 md:py-32">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <h3 className="text-4xl font-bold text-center text-white mb-4">Consultative Approach, Custom Solutions</h3>
                <p className="text-xl text-center text-gray-400 mb-16 max-w-3xl mx-auto">
                    Every solution is scoped and built from the ground up to fit your business, ensuring maximum ROI and long-term stability.
                </p>
                <div className="grid md:grid-cols-3 gap-8">
                    <ValueCard icon={Cpu} title="Intelligent Workflows" description="We integrate AI and custom scripts to manage complex decisions, moving beyond simple task lists." />
                    <ValueCard icon={TrendingUp} title="Affordable Scalability" description="Solutions leverage low-code and cloud services designed to grow with your SMB budget and complexity." />
                    <ValueCard icon={Shield} title="Full-Stack Infrastructure" description="We secure the foundation, handling everything from SSO setup to server optimization for guaranteed reliability." />
                </div>
            </div>
        </section>

        {/* Section 4: Solutions CTA - Prominent Footer CTA */}
        <section className="w-full bg-slate-900 py-20 text-center border-t border-b border-cyan-900">
            <h3 className="text-4xl font-bold text-white mb-4">See Our Full Areas of Solutions</h3>
            <p className="text-xl text-gray-400 mb-8 max-w-3xl mx-auto">
                Explore the three pillars of service we offer to transform your business operations.
            </p>
            <Link to="/solutions">
                <PrimaryButton className="bg-transparent border border-cyan-600 hover:bg-cyan-600">
                    View All Solutions
                    <ArrowRight className="h-5 w-5 ml-2" />
                </PrimaryButton>
            </Link>
        </section>
    </div>
);

/** Reusable Pain Card component for Home page - Updated to Cyan accent */
const PainCard = ({ icon: Icon, title, description }) => (
    <div className="bg-slate-800 rounded-xl p-6 border-t-4 border-cyan-600 flex flex-col items-center text-center shadow-2xl shadow-slate-900/50">
        <Icon className="h-8 w-8 text-cyan-400 mb-4" />
        <h4 className="text-xl font-semibold text-white mb-2">{title}</h4>
        <p className="text-gray-400">{description}</p>
    </div>
);

/** Reusable Value Card component for Home page - Updated to Cyan accent */
const ValueCard = ({ icon: Icon, title, description }) => (
    <div className="bg-slate-900 rounded-xl p-8 border border-slate-700 hover:border-cyan-600 transition duration-300 flex flex-col items-start h-full shadow-lg shadow-slate-900/50">
        <div className="p-3 bg-cyan-600/20 rounded-md mb-4">
            <Icon className="h-6 w-6 text-cyan-400" />
        </div>
        <h4 className="text-2xl font-semibold text-white mb-3">{title}</h4>
        <p className="text-gray-400 text-lg">{description}</p>
    </div>
);


/** Services Page View - Rewritten as Expertise Page - Updated to Slate/Cyan */
const Services = () => (
    <div className="pt-16 pb-20 max-w-7xl mx-auto bg-slate-900">
        <h1 className="text-5xl font-extrabold text-center text-white mb-4">Our Core Areas of Solutions</h1>
        <p className="text-xl text-center text-gray-400 mb-8 max-w-4xl mx-auto">
            We deliver tailored consulting and build custom solutions across these three foundational pillars of modern business efficiency.
        </p>
        <p className="text-lg font-semibold text-center text-cyan-400 mb-20 max-w-4xl mx-auto">
            **Pricing is custom-scoped per client project after a free initial audit.**
        </p>

        {/* --- SERVICE PILLARS GRID --- */}
        <div className="space-y-16 mb-24">
            {servicePillars.map((pillar, index) => (
                <PillarSection key={index} pillar={pillar} index={index} />
            ))}
        </div>

        {/* Final CTA to move to Contact page */}
        <div className="text-center mt-24 p-12 bg-slate-800 rounded-xl border border-cyan-700/50 shadow-2xl">
            <h3 className="text-4xl font-bold text-white mb-4">Ready to Build Your Custom Solution?</h3>
            <p className="text-xl text-gray-400 mb-8 max-w-3xl mx-auto">
                Start with a consultation where we define the exact scope, resources, and cost required to solve your unique business challenges.
            </p>
            <Link to="/contact">
                <PrimaryButton>
                    Start Your Project Inquiry &rarr;
                </PrimaryButton>
            </Link>
        </div>
    </div>
);

/** New Pillar Section Component - Updated to Slate/Cyan */
const PillarSection = ({ pillar, index }) => (
    <div className="bg-slate-800 rounded-xl p-8 lg:p-12 border-t-4 border-cyan-600 shadow-2xl">
        <div className="flex items-center mb-6">
            <pillar.icon className="h-10 w-10 text-cyan-400 mr-4 flex-shrink-0" />
            <div>
                <h2 className="text-2xl font-semibold text-cyan-400">{pillar.name}</h2>
                <p className="text-xl font-bold text-white">{pillar.description}</p>
            </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mt-8">
            {pillar.services.map((service, i) => (
                <ServiceDetailCard key={i} service={service} />
            ))}
        </div>
    </div>
);

/** New Service Detail Card for Pillars - Updated to Slate/Cyan */
const ServiceDetailCard = ({ service }) => (
    <div className="bg-slate-900 rounded-lg p-6 border border-slate-700 h-full flex flex-col shadow-lg hover:border-cyan-600 transition duration-300">
        <h4 className="text-lg font-bold text-white mb-2 flex items-center">
            <ArrowRight className="h-5 w-5 text-cyan-500 mr-2" />
            {service.name}
        </h4>
        <p className="text-gray-400 text-sm flex-grow">{service.detail}</p>
    </div>
);


/** About Us Page View - Updated to Slate/Cyan */
const About = () => (
    <div className="pt-16 pb-20 max-w-5xl mx-auto bg-slate-900">
        <h1 className="text-5xl font-extrabold text-center text-white mb-4">The Skyewire Philosophy</h1>
        <p className="text-xl text-center text-gray-400 mb-16">
            Enterprise-grade efficiency, delivered with a dedicated partner approach.
        </p>

        <div className="bg-slate-800/70 p-8 md:p-12 rounded-xl shadow-2xl space-y-10 border border-slate-700">
            {/* Mission Section */}
            <div>
                <h2 className="text-3xl font-bold text-white border-b border-cyan-700/50 pb-3 mb-6">Our Mission</h2>
                <p className="text-lg text-gray-300 leading-relaxed">
                    Skyewire Solutions was founded on a simple principle: **Operational efficiency should be accessible and affordable** for growing small and medium-sized businesses. We cut through the complexity of major enterprise systems, delivering high-impact, customized workflows using nimble, powerful tools like n8n and Make.com. We believe technology should work *for* your team, not create more work.
                </p>
            </div>

            {/* Founder Section */}
            <div>
                <h2 className="text-3xl font-bold text-white border-b border-cyan-700/50 pb-3 mb-6">Meet Cameron (Founder)</h2>
                <div className="flex flex-col md:flex-row items-center md:items-start space-y-6 md:space-y-0 md:space-x-8">
                    <div className="w-32 h-32 bg-cyan-600 rounded-full flex items-center justify-center text-2xl font-black text-white flex-shrink-0 border-4 border-cyan-400">
                        C.W.
                    </div>
                    <div className='flex-grow'>
                        <p className="text-gray-400 italic text-lg mb-4">
                            "I believe every minute spent on a repetitive task is a minute lost on strategic growth. My focus is on providing direct, expert partnership—no account managers, no layers of bureaucracy. Just clean, reliable, and profitable workflow solutions."
                        </p>
                        <p className="font-medium text-cyan-400">— Cameron, Automation & Infrastructure Consultant</p>
                    </div>
                </div>
            </div>
            
            {/* Solo Consultant Advantage */}
            <div>
                <h2 className="text-3xl font-bold text-white border-b border-cyan-700/50 pb-3 mb-6">The Solo Advantage</h2>
                <ul className="grid md:grid-cols-3 gap-6 text-gray-400">
                    <li className="p-4 bg-slate-900 rounded-lg border border-slate-700"><strong className="text-cyan-400">Direct Access:</strong> You work directly with the technical expert building your solution.</li>
                    <li className="p-4 bg-slate-900 rounded-lg border border-slate-700"><strong className="text-cyan-400">Low Overhead:</strong> High-value expertise without the expensive agency price tag.</li>
                    <li className="p-4 bg-slate-900 rounded-lg border border-slate-700"><strong className="text-cyan-400">Focused Dedication:</strong> Your project receives undivided attention and prioritized execution.</li>
                </ul>
            </div>
        </div>
    </div>
);


/** Thank You Page - New Dedicated Route */
const ThankYou = () => (
    <div className="pt-20 pb-20 max-w-3xl mx-auto bg-slate-900 text-center px-4">
        <div className="bg-slate-800 p-12 rounded-xl shadow-2xl border border-green-700/50">
            <Bot className="h-16 w-16 text-green-400 mx-auto mb-6" />
            <h1 className="text-5xl font-extrabold text-white mb-4">Thank You!</h1>
            <p className="text-xl text-gray-300 mb-8">
                Your audit request has been submitted successfully.
            </p>
            <p className="text-lg text-gray-400 mb-12">
                Cameron will be in touch shortly to define the scope of your custom solution and discuss next steps.
            </p>
            <div className="space-y-4">
                <p className="text-sm text-gray-500">
                    In the meantime, feel free to explore our solutions or return to the home page.
                </p>
                <div className="flex gap-4 justify-center">
                    <Link to="/">
                        <button className="px-8 py-3 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg transition">
                            Back to Home
                        </button>
                    </Link>
                    <Link to="/solutions">
                        <button className="px-8 py-3 bg-slate-700 hover:bg-slate-600 text-white font-semibold rounded-lg transition">
                            View Solutions
                        </button>
                    </Link>
                </div>
            </div>
        </div>
    </div>
);

/** Contact Page View - Updated to Slate/Cyan */
const Contact = () => {
    const navigate = useNavigate();
    const [status, setStatus] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setStatus(null);
        setErrorMessage('');

        // Get form data
        const formData = {
            name: e.target.name.value,
            email: e.target.email.value,
            painpoint: e.target.painpoint.value,
        };

        try {
            // Send to PHP backend
            const response = await fetch('/api/save-contact.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            const result = await response.json();

            if (!response.ok) {
                setStatus('error');
                setErrorMessage(result.error || 'Failed to submit form. Please try again.');
                setIsLoading(false);
                return;
            }

            // Track conversion
            trackFacebookEvent('Lead', { currency: 'USD' });
            trackGoogleEvent('form_submit', {
              form_name: 'contact_form',
              email: formData.email
            });

            // Navigate to thank you page
            setIsLoading(false);
            navigate('/thank-you');

        } catch (error) {
            console.error('Form submission error:', error);
            setStatus('error');
            setErrorMessage('Network error. Please check your connection and try again.');
            setIsLoading(false);
        }
    };

    return (
        <div className="pt-10 pb-20 max-w-3xl mx-auto bg-slate-900">
            <h1 className="text-4xl font-bold text-center text-white mb-4">Ready to Reclaim Your Time?</h1>
            <p className="text-xl text-center text-gray-400 mb-12">
                All of our solutions begin with a **personalized workflow audit** to define scope and cost.
            </p>

            <div className="bg-slate-800 p-8 rounded-xl shadow-2xl border border-cyan-700">
                {status === 'error' ? (
                    <div className="text-center p-8 bg-red-900/50 rounded-lg">
                        <X className="h-10 w-10 text-red-400 mx-auto mb-4" />
                        <h2 className="text-2xl font-bold text-white mb-2">Submission Error</h2>
                        <p className="text-red-200 mb-6">{errorMessage}</p>
                        <button
                            onClick={() => setStatus(null)}
                            className="px-6 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition"
                        >
                            Try Again
                        </button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Name Field */}
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-300">Full Name</label>
                            <input
                                type="text"
                                id="name"
                                required
                                className="mt-1 block w-full rounded-lg border-slate-600 bg-slate-900 text-white shadow-sm focus:border-cyan-500 focus:ring-cyan-500 p-3"
                                placeholder="Jane Doe"
                            />
                        </div>

                        {/* Email Field */}
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-300">Business Email</label>
                            <input
                                type="email"
                                id="email"
                                required
                                className="mt-1 block w-full rounded-lg border-slate-600 bg-slate-900 text-white shadow-sm focus:border-cyan-500 focus:ring-cyan-500 p-3"
                                placeholder="jane@yourcompany.com"
                            />
                        </div>

                        {/* Crucial Qualification Field */}
                        <div>
                            <label htmlFor="painpoint" className="block text-sm font-medium text-gray-300">
                                What is the most manual/repetitive task in your business? (Crucial for the Optimization Plan)
                            </label>
                            <textarea
                                id="painpoint"
                                rows="4"
                                required
                                className="mt-1 block w-full rounded-lg border-slate-600 bg-slate-900 text-white shadow-sm focus:border-cyan-500 focus:ring-cyan-500 p-3"
                                placeholder="e.g., Manually transferring 50 leads a day from one system to another."
                            ></textarea>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-cyan-600 hover:bg-cyan-700 transition duration-300 disabled:opacity-50"
                        >
                            {isLoading ? (
                                <>
                                    <Loader className="h-5 w-5 animate-spin mr-3" />
                                    Submitting...
                                </>
                            ) : (
                                'Submit for Free Workflow Audit'
                            )}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};

// --- Page Tracker Component ---
const PageTracker = () => {
    const location = useLocation();

    useEffect(() => {
        const pageNames = {
            '/': 'Home',
            '/solutions': 'Solutions',
            '/about': 'About Us',
            '/contact': 'Contact'
        };
        trackPageView(pageNames[location.pathname] || 'Unknown');
    }, [location.pathname]);

    return null;
};

// --- Main Application Component ---
const AppContent = () => {
    const [cookieConsent, setCookieConsent] = useState(false);

    // Initialize tracking on mount
    useEffect(() => {
        const prefs = CookieManager.getCookiePreferences();
        if (prefs) {
            setCookieConsent(true);
            if (prefs.analytics) initializeGoogleAnalytics();
            if (prefs.marketing) initializeFacebookPixel();
        }
    }, []);

    return (
        <div className="min-h-screen bg-slate-900 font-sans antialiased text-white">
            <PageTracker />
            <Header />
            <main>
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/solutions" element={<Services />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/thank-you" element={<ThankYou />} />
                </Routes>
            </main>
            <Footer />
            {!cookieConsent && <CookieConsent onConsent={() => setCookieConsent(true)} />}
        </div>
    );
};

const App = () => (
    <BrowserRouter>
        <AppContent />
    </BrowserRouter>
);

export default App;
